import { Inject, Injectable, InjectionToken } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Share, ShareOptions } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { CustomToastService } from './custom-toast.service';

export interface CopyTextOptions {
    successKey?: string;
    errorKey?: string;
}

/** Parte del plugin de Share que este service usa. */
export interface SharePlugin {
    share(options: ShareOptions): Promise<unknown>;
}

/**
 * `Share` de @capacitor/share es un Proxy creado por `registerPlugin`, así que
 * no se le puede aplicar `spyOn`. Inyectarlo por token es lo que hace testeable
 * la rama nativa sin depender del entorno.
 */
export const SHARE_PLUGIN = new InjectionToken<SharePlugin>('SHARE_PLUGIN', {
    providedIn: 'root',
    factory: () => Share,
});

const TOAST_DURATION_MS = 3000;

/** Instagram no acepta texto por URL: se copia y se abre la app para que el usuario pegue. */
const INSTAGRAM_URL = 'https://www.instagram.com/accounts/login/?next=/';
const INSTAGRAM_OPEN_DELAY_MS = 2100;

/**
 * Único punto de entrada para compartir y copiar texto.
 *
 * Antes esta lógica estaba duplicada en los 5 modales de share, en
 * `share-microsite-state.service` y en varios componentes de feature, con 3
 * formatos distintos de URL de WhatsApp y 3 copias del fallback de
 * `execCommand`. Los consumidores nuevos usan este service; la migración de los
 * modales existentes queda como deuda técnica aparte.
 */
@Injectable({ providedIn: 'root' })
export class ShareService {
    constructor(
        private readonly toast: CustomToastService,
        private readonly translate: TranslateService,
        @Inject(SHARE_PLUGIN) private readonly sharePlugin: SharePlugin,
    ) {}

    /**
     * Copia texto al portapapeles con feedback y degradación por capas:
     * `navigator.clipboard` → share nativo (si estamos en app) → toast de error.
     */
    async copyText(text: string, options: CopyTextOptions = {}): Promise<void> {
        const successKey = options.successKey ?? 'share.copy-success';
        const errorKey = options.errorKey ?? 'share.copy-error';

        if (this.isClipboardAvailable()) {
            try {
                await navigator.clipboard.writeText(text);
                this.showToast(successKey, 'success');
                return;
            } catch {
                // Sigue al fallback: el permiso de clipboard puede estar denegado.
            }
        }

        if (Capacitor.isNativePlatform()) {
            await this.shareNative(text);
            return;
        }

        this.showToast(errorKey, 'error');
    }

    shareWhatsApp(text: string, phone?: string): void {
        const recipient = phone ?? '';
        window.open(`https://wa.me/${recipient}?text=${encodeURIComponent(text)}`, '_blank');
    }

    shareEmail(text: string, subject: string): void {
        const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
        window.open(mailtoUrl, '_blank');
    }

    async shareInstagram(text: string): Promise<void> {
        await this.copyText(text, { successKey: 'share.instagram-copy-success' });

        setTimeout(() => window.open(INSTAGRAM_URL, '_blank'), INSTAGRAM_OPEN_DELAY_MS);
    }

    async shareNative(text: string): Promise<void> {
        try {
            await this.sharePlugin.share({
                title: this.translate.instant('share.native-title'),
                text,
                dialogTitle: this.translate.instant('share.native-dialog-title'),
            });
        } catch {
            // Cancelar el share del sistema no es un error a reportar.
        }
    }

    private isClipboardAvailable(): boolean {
        return !!(navigator.clipboard && typeof navigator.clipboard.writeText === 'function');
    }

    private showToast(key: string, type: 'success' | 'error'): void {
        this.toast.show(this.translate.instant(key), TOAST_DURATION_MS, type);
    }
}
